import { APP_ROLE_PARAM_KEY, Role, rolesManipulator, useGetUserRoles, useLogout } from 'auth';
import { ComponentType, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { GetUserDefaultRoute } from '@/modules/auth/utils/defaultRoute.tsx';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  ENTITY_ID_SEARCH_PARAM_KEY,
  ENTITY_IS_VERIFIED_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { Spinner } from 'ui';

export const persistentParams = [
  APPLY_REGISTER_ID,
  APPLY_REGISTER_CLASS_ID,
  APP_ROLE_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
  ENTITY_IS_VERIFIED_KEY,
  ENTITY_ID_SEARCH_PARAM_KEY,
];

export const getPersistentParamsSearch = (currentRole?: Role) => {
  const res = new URLSearchParams();
  const currentParams = new URLSearchParams(location.search);

  persistentParams.forEach(param => {
    const value = currentParams.get(param);
    if (value) res.set(param, value);
  });

  const manipulatorRole = rolesManipulator.current_user_role; //if logged but if not we lost app_role param

  if (res.has(APP_ROLE_PARAM_KEY) && manipulatorRole) {
    res.set(APP_ROLE_PARAM_KEY, manipulatorRole);
  } else if (currentRole) {
    res.set(APP_ROLE_PARAM_KEY, currentRole);
  }
  return res;
};

export function withPrivateAccess<T = object>(Wrapped: ComponentType<T>, role?: Role | Role[]) {
  return (props: T) => {
    const auth = useAuth();
    const logout = useLogout();
    const [, setParams] = useSearchParams();

    const userRole = rolesManipulator.current_user_role;
    const isUserRoleValid = Object.values(Role).includes(userRole as Role);
    const userRoles = useGetUserRoles('sr');
    const userHasRole = isUserRoleValid && userRoles.includes(userRole as Role);
    const isFetchingError = auth.error && auth.error.message === 'Failed to fetch';

    // if (isFetchingError && navigator.onLine) {
    //   // todo: in future use error code if it will be present
    //   console.log(auth.error); // todo: in future send error to sentry
    //   return <ErrorPage appRootPath={commonPaths.static.root} />;
    // }

    const shouldSigninRedirect = !isFetchingError && !auth.isLoading && !auth.isAuthenticated;
    const shouldRedirectToAppSelector =
      !auth.isLoading && auth.isAuthenticated && !userHasRole && !userRoles.length;
    const shouldSwitchRole =
      !auth.isLoading && auth.isAuthenticated && !userHasRole && !!userRoles.length;

    useEffect(() => {
      if (!isFetchingError) return;
      void logout(window.location.origin);
    }, [isFetchingError, logout]);

    useEffect(() => {
      if (!shouldSigninRedirect) return;
      auth.signinRedirect({
        redirect_uri: `${location.origin}${location.pathname}?${getPersistentParamsSearch(
          userRole || userRoles?.[0],
        ).toString()}`,
      });
    }, [auth, shouldSigninRedirect, userRole, userRoles]);

    useEffect(() => {
      if (!shouldRedirectToAppSelector) return;
      window.open(import.meta.env.VITE_APP_SELECTOR_URL, '_self');
    }, [shouldRedirectToAppSelector]);

    useEffect(() => {
      if (!shouldSwitchRole) return;
      setParams(prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set(APP_ROLE_PARAM_KEY, userRoles?.[0]);
        return nextParams;
      }, { replace: false });
    }, [setParams, shouldSwitchRole, userRoles]);

    if (
      (auth.isLoading && !auth.isAuthenticated && !auth.error) ||
      isFetchingError ||
      shouldSigninRedirect ||
      shouldRedirectToAppSelector ||
      shouldSwitchRole
    ) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      );
    }

    const resolveRoles = !role || Array.isArray(role) ? role : [role];
    const userHasAccess = userRole && resolveRoles?.includes(userRole);

    if (!userHasAccess && auth.isAuthenticated) {
      toast.error('You are not authorised to access this page', {
        id: 'access-denied',
      });
      return <GetUserDefaultRoute role={rolesManipulator.current_user_role as Role} />;
    }

    return <Wrapped {...(props as any)} />;
  };
}
